// packages/web-ui/src/components/file-tree/filter.ts
//
// Story W3-S5 AC #3 — combined chip-filter + search filter for the file
// tree. Pure function: takes the full tree + filters and returns the
// already-flattened-and-filtered visible row list.
//
// Visibility rules per AC #3.4:
// - File row visible iff:
//     (chipFilter === 'All' OR node.ext === chipFilter.replace('.', ''))
//     AND
//     (query === '' OR node.name.toLowerCase().includes(query.toLowerCase()))
// - Dir row visible iff at least one descendant file is visible
//   OR the dir's name itself matches the query
// - Folders that match the search query but have no matching children also
//   show (so users can navigate to a matching folder name).
//
// This function does NOT honour expansion state — the consumer applies
// expansion via `flattenTree` AFTER the filter has shrunk the tree shape.
// When a non-empty filter is active, all surviving dirs are auto-expanded.

import type { FileTreeNode } from '../../api/clients/files.js';
import type { ChipFilter } from '../../state/files.js';

function chipExt(chip: ChipFilter): string | null {
  if (chip === 'All') return null;
  return chip.replace(/^\./, '');
}

function fileMatches(
  node: FileTreeNode,
  ext: string | null,
  query: string,
): boolean {
  if (node.kind !== 'file') return false;
  if (ext !== null && node.ext !== ext) return false;
  if (query.length === 0) return true;
  return node.name.toLowerCase().includes(query);
}

function dirMatchesQuery(node: FileTreeNode, query: string): boolean {
  if (query.length === 0) return false;
  return node.kind === 'directory' && node.name.toLowerCase().includes(query);
}

/**
 * Walk-and-prune. Returns a copy of `nodes` with non-visible files dropped
 * and dirs without any visible descendants dropped (unless the dir's own
 * name matches the query). Always returns a fresh array.
 */
export function filterTree(
  nodes: readonly FileTreeNode[],
  chip: ChipFilter,
  rawQuery: string,
): FileTreeNode[] {
  const ext = chipExt(chip);
  const query = rawQuery.trim().toLowerCase();
  if (ext === null && query.length === 0) {
    // Identity case — return shallow copy so callers can rely on a fresh ref.
    return Array.from(nodes);
  }

  function recurse(list: readonly FileTreeNode[]): FileTreeNode[] {
    const out: FileTreeNode[] = [];
    for (const node of list) {
      if (node.kind === 'file') {
        if (fileMatches(node, ext, query)) out.push(node);
      } else {
        const children = node.children ?? [];
        const filtered = recurse(children);
        const dirByName = dirMatchesQuery(node, query);
        if (filtered.length > 0 || dirByName) {
          out.push({ ...node, children: filtered });
        }
      }
    }
    return out;
  }

  return recurse(nodes);
}

/**
 * True when ANY filter (chip or query) is active. Consumers use this to
 * auto-expand surviving dirs without mutating the user's expansion state.
 */
export function isFilterActive(chip: ChipFilter, rawQuery: string): boolean {
  return chip !== 'All' || rawQuery.trim().length > 0;
}

/**
 * Highlight helper — returns a list of `{text, match}` segments for a
 * row's `name` so the consumer can render `<span class="match">…</span>`
 * around the matching substring. Returns a single non-match segment when
 * `query` is empty.
 */
export interface NameSegment {
  text: string;
  match: boolean;
}

export function splitForHighlight(name: string, query: string): NameSegment[] {
  const q = query.trim().toLowerCase();
  if (q.length === 0) return [{ text: name, match: false }];
  const lower = name.toLowerCase();
  const out: NameSegment[] = [];
  let idx = 0;
  for (;;) {
    const found = lower.indexOf(q, idx);
    if (found === -1) {
      if (idx < name.length) out.push({ text: name.slice(idx), match: false });
      break;
    }
    if (found > idx) out.push({ text: name.slice(idx, found), match: false });
    out.push({ text: name.slice(found, found + q.length), match: true });
    idx = found + q.length;
  }
  return out;
}
